"use client";

import { Bot, Check, Copy, Loader2, Plus, Trash2 } from "lucide-react";
import { useFormatter, useLocale, useNow, useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { CopyBlock } from "@/components/agents/CopyBlock";
import { SettingsSection } from "@/components/settings/SettingsSection";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { Dialog } from "@/components/shared/Dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "@/i18n/navigation";
import { translateServerString } from "@/lib/server-strings/client";

type AgentKind = "key" | "oauth" | "link" | "email";

interface AgentInfo {
  id: string;
  name: string;
  kind: AgentKind;
  prefix: string;
  createdAt: string;
  lastUsedAt: string | null;
}

function CopyField({ value, label }: { value: string; label: string }) {
  const t = useTranslations("agents");
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="flex gap-2">
      <Input readOnly value={value} aria-label={label} className="font-mono text-sm" />
      <Button
        variant="outline"
        onClick={handleCopy}
        className="flex-shrink-0"
        title={copied ? t("copied") : t("copy")}
      >
        {copied ? <Check className="w-4 h-4 text-primary" /> : <Copy className="w-4 h-4" />}
        <span className="sr-only">{copied ? t("copied") : t("copy")}</span>
      </Button>
    </div>
  );
}

export function AgentsSection() {
  const t = useTranslations("agents");
  const tPage = useTranslations("agentsPage");
  const tCommon = useTranslations("common");
  const format = useFormatter();
  const locale = useLocale();
  const now = useNow({ updateInterval: 60_000 });
  const [agents, setAgents] = useState<AgentInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isNaming, setIsNaming] = useState(false);
  const [name, setName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [removing, setRemoving] = useState<AgentInfo | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [serverUrl, setServerUrl] = useState("");

  const kindLabels: Record<AgentKind, string> = {
    key: t("kindKey"),
    oauth: t("kindOauth"),
    link: t("kindLink"),
    email: t("kindEmail"),
  };

  useEffect(() => {
    setServerUrl(`${window.location.origin}/mcp`);

    const fetchAgents = async () => {
      try {
        const response = await fetch("/api/agents/keys");
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setAgents(data.agents || []);
      } catch (error) {
        console.error("Error fetching agents:", error);
        toast.error(t("failedToLoad"));
      } finally {
        setIsLoading(false);
      }
    };
    fetchAgents();
  }, [t]);

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setIsCreating(true);
    try {
      const response = await fetch("/api/agents/keys", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: trimmed }),
      });
      const data = await response.json();
      if (!response.ok) {
        toast.error(translateServerString(data.error, { agents: t, common: tCommon }));
        return;
      }
      setAgents((prev) => [data.agent, ...prev]);
      setNewKey(data.key);
      setName("");
      setIsNaming(false);
    } catch (error) {
      console.error(error);
      toast.error(t("failedToCreate"));
    } finally {
      setIsCreating(false);
    }
  };

  const handleRemove = async () => {
    if (!removing) return;
    setIsRemoving(true);
    try {
      const response = await fetch(`/api/agents/keys/${removing.id}`, { method: "DELETE" });
      if (!response.ok && response.status !== 404) {
        const data = await response.json().catch(() => ({}));
        toast.error(
          data.error
            ? translateServerString(data.error, { agents: t, common: tCommon })
            : t("failedToRemove")
        );
        return;
      }
      setAgents((prev) => prev.filter((agent) => agent.id !== removing.id));
      toast.success(t("removed"));
      setRemoving(null);
    } catch (error) {
      console.error(error);
      toast.error(t("failedToRemove"));
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <SettingsSection className="space-y-5">
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Bot className="w-4 h-4 text-primary" />
          <h3 className="font-semibold">{t("connected")}</h3>
        </div>
        <p className="text-sm text-muted-foreground">{t("description")}</p>
      </div>

      {serverUrl && (
        <div className="space-y-2">
          <p className="text-sm font-medium">{t("promptTitle")}</p>
          <CopyBlock
            text={tPage("askPrompt", { origin: serverUrl.replace(/\/mcp$/, "") })}
            label={t("promptTitle")}
          />
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-2">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : agents.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("noAgents")}</p>
      ) : (
        <div className="space-y-2">
          {agents.map((agent) => (
            <div
              key={agent.id}
              className="flex items-center justify-between gap-2 p-3 bg-secondary/50 rounded-lg"
            >
              <div className="flex flex-col gap-1 min-w-0 flex-1">
                <span className="text-sm font-medium truncate">{agent.name}</span>
                <span className="text-xs text-muted-foreground truncate">
                  {kindLabels[agent.kind] ?? kindLabels.key}
                  {" · "}
                  {agent.lastUsedAt
                    ? t("lastUsed", { time: format.relativeTime(new Date(agent.lastUsedAt), now) })
                    : t("neverUsed")}
                </span>
                <span className="text-xs text-muted-foreground truncate">
                  {t("connectedOn", {
                    date: new Date(agent.createdAt).toLocaleDateString(locale, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    }),
                  })}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setRemoving(agent)}
                title={t("remove")}
                className="flex-shrink-0"
              >
                <Trash2 className="size-5 text-destructive" />
                <span className="sr-only">{t("remove")}</span>
              </Button>
            </div>
          ))}
        </div>
      )}

      <details className="group text-sm">
        <summary className="cursor-pointer select-none text-muted-foreground">
          {t("developers")}
        </summary>
        <div className="mt-3 space-y-4">
          <p className="text-xs text-muted-foreground">
            {t("developersHint")}{" "}
            <Link href="/agents" className="text-primary underline underline-offset-2">
              {t("howToConnect")}
            </Link>
          </p>
          <div>
            <Label htmlFor="agents-server-address" className="text-sm">
              {t("serverAddress")}
            </Label>
            <p className="text-xs text-muted-foreground mt-1 mb-2">{t("serverAddressHint")}</p>
            {serverUrl && <CopyField value={serverUrl} label={t("serverAddress")} />}
          </div>
          {isNaming ? (
            <div>
              <Label htmlFor="agent-key-name" className="text-sm">
                {t("keyName")}
              </Label>
              <div className="flex gap-2 mt-2">
                <Input
                  id="agent-key-name"
                  value={name}
                  maxLength={60}
                  autoFocus
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("keyNamePlaceholder")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleCreate();
                    }
                    if (e.key === "Escape") setIsNaming(false);
                  }}
                  disabled={isCreating}
                />
                <Button
                  onClick={handleCreate}
                  disabled={isCreating || !name.trim()}
                  className="flex-shrink-0"
                >
                  {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : t("create")}
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" onClick={() => setIsNaming(true)} className="w-full">
              <Plus className="w-4 h-4" />
              {t("createKey")}
            </Button>
          )}
        </div>
      </details>

      <Dialog
        isOpen={newKey !== null}
        onClose={() => setNewKey(null)}
        title={t("newKeyTitle")}
        description={t("newKeyDescription")}
        disableOutsideClick
        footer={
          <Button onClick={() => setNewKey(null)} className="w-full sm:w-auto">
            {t("done")}
          </Button>
        }
      >
        {newKey && (
          <div className="space-y-4">
            <CopyField value={newKey} label={t("newKeyTitle")} />
            <div>
              <p className="text-sm font-medium mb-2">{t("claudeCodeCommand")}</p>
              <CopyField
                value={`claude mcp add --transport http nombox ${serverUrl} --header "Authorization: Bearer ${newKey}"`}
                label={t("claudeCodeCommand")}
              />
            </div>
          </div>
        )}
      </Dialog>

      <ConfirmDialog
        isOpen={removing !== null}
        onClose={() => !isRemoving && setRemoving(null)}
        onConfirm={handleRemove}
        title={t("removeConfirmTitle", { name: removing?.name ?? "" })}
        description={t("removeConfirmDescription")}
        confirmText={t("remove")}
        confirmIcon={<Trash2 className="size-4" />}
        variant="destructive"
        loading={isRemoving}
        autoCloseOnConfirm={false}
      />
    </SettingsSection>
  );
}
